import { Button, Input, Modal } from "@legal-500/legal500-design-system";
import { useEffect, useState } from "react";
import { dbManagerService } from "../../../api/dbManager.service.js";
import LoadingState from "../../../components/LoadingState/index.js";
import styles from "./DbManager.module.css";

const SEVEN_DAYS_SECONDS = 7 * 24 * 3600;

type PreviewLinkModalProps = {
  isOpen: boolean;
  label: string;
  family: string;
  onClose: () => void;
};

export function PreviewLinkModal({ isOpen, label, family, onClose }: PreviewLinkModalProps) {
  const [url, setUrl] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">("idle");

  useEffect(() => {
    if (!isOpen) {
      setUrl(null);
      setExpiresAt(null);
      setError(null);
      setCopyState("idle");
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    dbManagerService
      .generatePreviewLink(label, SEVEN_DAYS_SECONDS, family)
      .then(result => {
        if (cancelled) return;
        setUrl(result.url);
        setExpiresAt(result.expiresAt);
      })
      .catch(err => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, label, family]);

  const handleCopy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopyState("copied");
      setTimeout(() => setCopyState("idle"), 1500);
    } catch {
      setCopyState("failed");
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Preview link" size="medium" blurBackground>
      <div className={styles.modalBody}>
        {loading && <LoadingState message="Generating preview link..." />}
        {error && <div className={styles.errorBanner}>{error}</div>}

        {url && !loading && (
          <>
            <div className={styles.formField}>
              <label className={styles.fieldLabel} htmlFor="preview-link-url">
                Preview URL (valid 7 days)
              </label>
              <Input
                id="preview-link-url"
                className={styles.previewLinkInput}
                value={url}
                readOnly
                onFocus={event => event.currentTarget.select()}
              />
              {expiresAt && (
                <span className={styles.fieldHelper}>
                  Expires: {new Date(expiresAt).toLocaleString()}
                </span>
              )}
            </div>

            <div className={styles.actions}>
              <Button variant="outlined" color="primary" onClick={handleCopy}>
                {copyState === "copied"
                  ? "Copied!"
                  : copyState === "failed"
                    ? "Copy failed"
                    : "Copy to clipboard"}
              </Button>
              <Button variant="contained" color="default" onClick={onClose}>
                Close
              </Button>
            </div>
          </>
        )}

        {!url && !loading && error && (
          <div className={styles.actions}>
            <Button variant="contained" color="default" onClick={onClose}>
              Close
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}
